import type {
	ChatCardToolbarActions,
	ChatCardToolbarProps,
} from "../chat-panel/components/ChatCardToolbar";

export const DOCUMENT_PREVIEW_EVENT = "document_preview";

/**
 * The toolbar of the card the document was opened from, which the pane repeats
 * beneath it: the same artifact, so the same saving, downloading and rating.
 */
export type DocumentPreviewToolbar = Omit<
	ChatCardToolbarProps,
	"padTop" | "onCopy"
>;

/** What the slide-out pane needs to show a page of a document's PDF. */
export type DocumentPreviewPanelContent = {
	/** PDF URL signed for inline display, not as a download. */
	url: string;
	/** 1-based page to show. */
	page: number;
	/** Total pages, for the pane's caption. */
	totalPages: number;
	title: string;
	/** The document's summary, read under its title rather than on the card. */
	summary?: string;
	/** Absent where the document was opened from something without one. */
	toolbar?: DocumentPreviewToolbar;
	/**
	 * What the card's toolbar was allowed to offer. The pane is rendered outside
	 * the conversation, beyond the reach of the provider that decided this for
	 * the card, so its copy of the toolbar has to be told.
	 */
	toolbarActions?: ChatCardToolbarActions;
};

export type DocumentPreviewEventDetail = {
	type: typeof DOCUMENT_PREVIEW_EVENT;
	/** Optional because anything may dispatch on the shared `chatevent` bus. */
	content?: { is_open: boolean } & DocumentPreviewPanelContent;
};

export const DOCUMENT_PREVIEW_PAGE_EVENT = "document-preview:page-shown";

export type DocumentPreviewPageShownDetail = {
	/** The document being read, as the card handed it to the pane. */
	url: string;
	/** 1-based page the reader has reached. */
	page: number;
};

/**
 * The pane saying which page is in view, so the strip a document was opened
 * from can follow the reading as well as lead it.
 */
export function emitDocumentPreviewPageShown(
	detail: DocumentPreviewPageShownDetail,
) {
	document.dispatchEvent(
		new CustomEvent<DocumentPreviewPageShownDetail>(
			DOCUMENT_PREVIEW_PAGE_EVENT,
			{ detail },
		),
	);
}

/*
 * Both of these are dispatched on `document`, the same bus the video pane and
 * profile panel use, so an artifact deep in the conversation can reach the pane
 * without threading props to it.
 */

const dispatch = (content: DocumentPreviewPanelContent, isOpen: boolean) => {
	const detail: DocumentPreviewEventDetail = {
		type: DOCUMENT_PREVIEW_EVENT,
		content: { is_open: isOpen, ...content },
	};
	document.dispatchEvent(new CustomEvent("chatevent", { detail }));
};

/** Opens the chat layout's slide-out pane on a page of a document. */
export function openDocumentPreviewPanel(content: DocumentPreviewPanelContent) {
	dispatch(content, true);
}

/**
 * Turns the pane to a page of the document it is already showing, and does
 * nothing at all otherwise. Stepping along a thumbnail strip is how a reader
 * pages through a document they have open; it is not how they open one.
 */
export function showDocumentPreviewPage(content: DocumentPreviewPanelContent) {
	dispatch(content, false);
}
